// ============================================================================
// Python du lycée — analyse (refonte lot 5.7, moteur v28).
// ============================================================================
// Descente récursive sur les jetons de `tokenize`. Sous-ensemble : affectations
// (simples, multiples, `a, b = b, a % b`, `L[i] = v`, `+=`…), if / elif / else,
// while, for … in, def / return, print, import de math, listes (compréhensions
// simples, tranches), expressions arithmétiques, comparaisons chaînées, and / or /
// not, expression conditionnelle. Rien d'autre : une construction inconnue est une
// erreur claire, jamais une exécution approximative.
// ============================================================================

import type { BinOp, CmpOp, Expr, Module, Stmt, Target } from './ast';
import { PyError } from './errors';
import { tokenize, type Tok } from './lexer';

const KEYWORDS = new Set([
  'if',
  'elif',
  'else',
  'while',
  'for',
  'in',
  'def',
  'return',
  'and',
  'or',
  'not',
  'pass',
  'break',
  'continue',
  'import',
  'from',
  'as',
  'is',
  'lambda',
  'class',
  'try',
  'except',
  'with',
  'global',
  'nonlocal',
  'yield',
  'del',
  'assert',
  'raise',
  'True',
  'False',
  'None',
]);

const UNSUPPORTED = new Set([
  'lambda',
  'class',
  'try',
  'except',
  'with',
  'global',
  'nonlocal',
  'yield',
  'del',
  'assert',
  'raise',
  'is',
  'as',
]);

const AUG: Record<string, BinOp> = {
  '+=': '+',
  '-=': '-',
  '*=': '*',
  '/=': '/',
  '//=': '//',
  '%=': '%',
  '**=': '**',
};

class Parser {
  private i = 0;
  constructor(private readonly toks: Tok[]) {}

  private get tok(): Tok {
    return this.toks[this.i]!;
  }
  private kind(): Tok['t'] {
    return this.toks[this.i]!.t;
  }
  private peek(n = 1): Tok {
    return this.toks[Math.min(this.i + n, this.toks.length - 1)]!;
  }
  private isOp(v: string): boolean {
    return this.tok.t === 'op' && this.tok.v === v;
  }
  private isKw(v: string): boolean {
    return this.tok.t === 'name' && this.tok.v === v;
  }
  private fail(msg: string, line = this.tok.line): never {
    throw new PyError('syntax', msg, line);
  }
  private expectOp(v: string): Tok {
    if (!this.isOp(v)) this.fail(`« ${v} » attendu.`);
    return this.toks[this.i++]!;
  }
  private expectKw(v: string): void {
    if (!this.isKw(v)) this.fail(`« ${v} » attendu.`);
    this.i++;
  }
  private expectName(): Tok {
    const t = this.tok;
    if (t.t !== 'name' || KEYWORDS.has(t.v)) this.fail('Nom attendu.');
    this.i++;
    return t;
  }

  module(): Module {
    const body: Stmt[] = [];
    while (this.tok.t !== 'eof') {
      if (this.tok.t === 'newline') {
        this.i++;
        continue;
      }
      if (this.tok.t === 'indent') this.fail('Indentation inattendue.');
      body.push(...this.statement());
    }
    return { body };
  }

  private statement(): Stmt[] {
    const t = this.tok;
    if (t.t === 'name' && UNSUPPORTED.has(t.v))
      throw new PyError('unsupported', `« ${t.v} » n’est pas pris en charge.`, t.line);
    if (this.isKw('if')) return [this.ifStmt()];
    if (this.isKw('while')) return [this.whileStmt()];
    if (this.isKw('for')) return [this.forStmt()];
    if (this.isKw('def')) return [this.defStmt()];
    return this.simpleLine();
  }

  private block(): Stmt[] {
    this.expectOp(':');
    if (this.tok.t !== 'newline') return this.simpleLine();
    this.i++;
    if (this.toks[this.i]!.t !== 'indent') this.fail('Bloc indenté attendu.');
    this.i++;
    const body: Stmt[] = [];
    while (this.kind() !== 'dedent' && this.kind() !== 'eof') {
      if (this.kind() === 'newline') {
        this.i++;
        continue;
      }
      body.push(...this.statement());
    }
    if (this.kind() === 'dedent') this.i++;
    if (!body.length) this.fail('Bloc vide.');
    return body;
  }

  private ifStmt(): Stmt {
    const line = this.tok.line;
    this.i++;
    const test = this.expr();
    const body = this.block();
    let orelse: Stmt[] = [];
    if (this.isKw('elif')) {
      const s = this.ifStmt();
      orelse = [{ ...(s as Extract<Stmt, { k: 'if' }>), elif: true }];
    } else if (this.isKw('else')) {
      this.i++;
      orelse = this.block();
    }
    return { k: 'if', test, body, orelse, line };
  }

  private whileStmt(): Stmt {
    const line = this.tok.line;
    this.i++;
    const test = this.expr();
    const body = this.block();
    if (this.isKw('else'))
      throw new PyError('unsupported', '« while … else » n’est pas pris en charge.', this.tok.line);
    return { k: 'while', test, body, line };
  }

  private forStmt(): Stmt {
    const line = this.tok.line;
    this.i++;
    const target = this.targetList();
    this.expectKw('in');
    const iter = this.exprList();
    const body = this.block();
    if (this.isKw('else'))
      throw new PyError('unsupported', '« for … else » n’est pas pris en charge.', this.tok.line);
    return { k: 'for', target, iter, body, line };
  }

  private defStmt(): Stmt {
    const line = this.tok.line;
    this.i++;
    const name = this.expectName().v;
    this.expectOp('(');
    const params: string[] = [];
    while (!this.isOp(')')) {
      params.push(this.expectName().v);
      if (this.isOp(':'))
        throw new PyError(
          'unsupported',
          'Les annotations de type ne sont pas prises en charge.',
          this.tok.line,
        );
      if (this.isOp('='))
        throw new PyError(
          'unsupported',
          'Les valeurs par défaut ne sont pas prises en charge.',
          this.tok.line,
        );
      if (!this.isOp(')')) this.expectOp(',');
    }
    this.i++;
    if (this.isOp('->'))
      throw new PyError(
        'unsupported',
        'Les annotations de type ne sont pas prises en charge.',
        this.tok.line,
      );
    if (new Set(params).size !== params.length) this.fail('Paramètre en double.', line);
    const body = this.block();
    return { k: 'def', name, params, body, line };
  }

  /** instructions simples d'une ligne, séparées par « ; » */
  private simpleLine(): Stmt[] {
    const out: Stmt[] = [this.small()];
    while (this.isOp(';')) {
      this.i++;
      if (this.tok.t === 'newline') break;
      out.push(this.small());
    }
    if (this.tok.t !== 'newline') this.fail('Fin de ligne attendue.');
    this.i++;
    return out;
  }

  private small(): Stmt {
    const t = this.tok;
    const line = t.line;
    if (t.t === 'name' && UNSUPPORTED.has(t.v))
      throw new PyError('unsupported', `« ${t.v} » n’est pas pris en charge.`, line);
    if (this.isKw('pass')) {
      this.i++;
      return { k: 'pass', line };
    }
    if (this.isKw('break')) {
      this.i++;
      return { k: 'break', line };
    }
    if (this.isKw('continue')) {
      this.i++;
      return { k: 'continue', line };
    }
    if (this.isKw('return')) {
      this.i++;
      if (this.tok.t === 'newline' || this.isOp(';')) return { k: 'return', value: null, line };
      return { k: 'return', value: this.exprList(), line };
    }
    if (this.isKw('import')) {
      this.i++;
      const names = [this.expectName().v];
      while (this.isOp(',')) {
        this.i++;
        names.push(this.expectName().v);
      }
      return { k: 'import', names, from: null, line };
    }
    if (this.isKw('from')) {
      this.i++;
      const mod = this.expectName().v;
      this.expectKw('import');
      const names: string[] = [];
      if (this.isOp('*')) {
        this.i++;
        names.push('*');
      } else {
        names.push(this.expectName().v);
        while (this.isOp(',')) {
          this.i++;
          names.push(this.expectName().v);
        }
      }
      return { k: 'import', names, from: mod, line };
    }
    const first = this.exprList();
    if (this.tok.t === 'op' && AUG[this.tok.v]) {
      const op = AUG[this.tok.v]!;
      this.i++;
      const target = toTarget(first);
      if (target.k === 'tuple') this.fail('Affectation augmentée sur plusieurs variables.', line);
      return { k: 'aug', target, op, value: this.expr(), line };
    }
    if (this.isOp('=')) {
      const targets: Target[] = [toTarget(first)];
      let value: Expr = first;
      while (this.isOp('=')) {
        this.i++;
        value = this.exprList();
        if (this.isOp('=')) targets.push(toTarget(value));
      }
      return { k: 'assign', targets, value, line };
    }
    return { k: 'expr', e: first, line };
  }

  private targetList(): Target {
    const line = this.tok.line;
    const items: Target[] = [toTarget(this.primaryOrParen())];
    let tuple = false;
    while (this.isOp(',')) {
      tuple = true;
      this.i++;
      if (this.isKw('in')) break;
      items.push(toTarget(this.primaryOrParen()));
    }
    return tuple ? { k: 'tuple', items, line } : items[0]!;
  }

  private primaryOrParen(): Expr {
    return this.primary();
  }

  /** suite d'expressions séparées par des virgules (un tuple s'il y en a plusieurs) */
  private exprList(): Expr {
    const line = this.tok.line;
    const first = this.expr();
    if (!this.isOp(',')) return first;
    const items = [first];
    while (this.isOp(',')) {
      this.i++;
      if (this.endsList()) break;
      items.push(this.expr());
    }
    return { k: 'tuple', items, line };
  }
  private endsList(): boolean {
    const t = this.tok;
    return (
      t.t === 'newline' ||
      (t.t === 'op' && [')', ']', '=', ';', ':'].includes(t.v)) ||
      (t.t === 'op' && !!AUG[t.v])
    );
  }

  expr(): Expr {
    const line = this.tok.line;
    const body = this.orTest();
    if (this.isKw('if')) {
      this.i++;
      const test = this.orTest();
      this.expectKw('else');
      const orelse = this.expr();
      return { k: 'ifexp', test, body, orelse, line };
    }
    return body;
  }
  private orTest(): Expr {
    let l = this.andTest();
    while (this.isKw('or')) {
      const line = this.tok.line;
      this.i++;
      l = { k: 'bool', op: 'or', l, r: this.andTest(), line };
    }
    return l;
  }
  private andTest(): Expr {
    let l = this.notTest();
    while (this.isKw('and')) {
      const line = this.tok.line;
      this.i++;
      l = { k: 'bool', op: 'and', l, r: this.notTest(), line };
    }
    return l;
  }
  private notTest(): Expr {
    if (this.isKw('not')) {
      const line = this.tok.line;
      this.i++;
      return { k: 'unary', op: 'not', e: this.notTest(), line };
    }
    return this.comparison();
  }
  private cmpOp(): CmpOp | null {
    const t = this.tok;
    if (t.t === 'op' && ['<', '>', '<=', '>=', '==', '!='].includes(t.v)) return t.v as CmpOp;
    if (this.isKw('in')) return 'in';
    if (this.isKw('not') && this.peek().t === 'name' && this.peek().v === 'in') return 'not in';
    if (this.isKw('is'))
      throw new PyError('unsupported', '« is » n’est pas pris en charge.', t.line);
    return null;
  }
  private comparison(): Expr {
    const line = this.tok.line;
    const first = this.arith();
    const ops: CmpOp[] = [];
    const rest: Expr[] = [];
    for (let op = this.cmpOp(); op; op = this.cmpOp()) {
      this.i += op === 'not in' ? 2 : 1;
      ops.push(op);
      rest.push(this.arith());
    }
    return ops.length ? { k: 'compare', first, ops, rest, line } : first;
  }
  private arith(): Expr {
    let l = this.term();
    while (this.isOp('+') || this.isOp('-')) {
      const t = this.toks[this.i++]!;
      l = { k: 'bin', op: t.v as BinOp, l, r: this.term(), line: t.line };
    }
    return l;
  }
  private term(): Expr {
    let l = this.factor();
    while (this.isOp('*') || this.isOp('/') || this.isOp('//') || this.isOp('%')) {
      const t = this.toks[this.i++]!;
      l = { k: 'bin', op: t.v as BinOp, l, r: this.factor(), line: t.line };
    }
    return l;
  }
  private factor(): Expr {
    if (this.isOp('-') || this.isOp('+')) {
      const t = this.toks[this.i++]!;
      return { k: 'unary', op: t.v as '-' | '+', e: this.factor(), line: t.line };
    }
    return this.power();
  }
  private power(): Expr {
    const base = this.primary();
    if (this.isOp('**')) {
      const t = this.toks[this.i++]!;
      return { k: 'bin', op: '**', l: base, r: this.factor(), line: t.line };
    }
    return base;
  }
  private primary(): Expr {
    let e = this.atom();
    for (;;) {
      if (this.isOp('(')) {
        const line = this.tok.line;
        this.i++;
        const args: Expr[] = [];
        while (!this.isOp(')')) {
          if (this.tok.t === 'name' && this.peek().t === 'op' && this.peek().v === '=')
            throw new PyError(
              'unsupported',
              'Les arguments nommés ne sont pas pris en charge.',
              this.tok.line,
            );
          args.push(this.expr());
          if (!this.isOp(')')) this.expectOp(',');
        }
        this.i++;
        e = { k: 'call', f: e, args, line };
      } else if (this.isOp('[')) {
        const line = this.tok.line;
        this.i++;
        let lo: Expr | null = null;
        if (!this.isOp(':')) lo = this.expr();
        if (this.isOp(':')) {
          this.i++;
          const hi = this.isOp(']') || this.isOp(':') ? null : this.expr();
          let step: Expr | null = null;
          if (this.isOp(':')) {
            this.i++;
            step = this.isOp(']') ? null : this.expr();
          }
          this.expectOp(']');
          e = { k: 'slice', e, lo, hi, step, line };
        } else {
          this.expectOp(']');
          e = { k: 'index', e, i: lo!, line };
        }
      } else if (this.isOp('.')) {
        const line = this.tok.line;
        this.i++;
        e = { k: 'attr', e, name: this.expectName().v, line };
      } else return e;
    }
  }
  private atom(): Expr {
    const t = this.tok;
    const line = t.line;
    if (t.t === 'num') {
      this.i++;
      return { k: 'num', text: t.v, line };
    }
    if (t.t === 'str') {
      this.i++;
      let v = t.v;
      while (this.tok.t === 'str') v += this.toks[this.i++]!.v;
      return { k: 'str', v, line };
    }
    if (t.t === 'name') {
      if (t.v === 'True' || t.v === 'False' || t.v === 'None') {
        this.i++;
        return { k: 'const', v: t.v, line };
      }
      if (UNSUPPORTED.has(t.v))
        throw new PyError('unsupported', `« ${t.v} » n’est pas pris en charge.`, line);
      if (KEYWORDS.has(t.v)) this.fail(`« ${t.v} » inattendu.`);
      this.i++;
      return { k: 'name', id: t.v, line };
    }
    if (this.isOp('(')) {
      this.i++;
      if (this.isOp(')')) {
        this.i++;
        return { k: 'tuple', items: [], line };
      }
      const first = this.expr();
      if (this.isOp(')')) {
        this.i++;
        return first;
      }
      const items = [first];
      while (this.isOp(',')) {
        this.i++;
        if (this.isOp(')')) break;
        items.push(this.expr());
      }
      this.expectOp(')');
      return { k: 'tuple', items, line };
    }
    if (this.isOp('[')) {
      this.i++;
      const items: Expr[] = [];
      if (this.isOp(']')) {
        this.i++;
        return { k: 'list', items, line };
      }
      const first = this.expr();
      if (this.isKw('for')) {
        this.i++;
        const target = this.targetList();
        this.expectKw('in');
        const iter = this.orTest();
        let cond: Expr | null = null;
        if (this.isKw('if')) {
          this.i++;
          cond = this.orTest();
        }
        if (this.isKw('for'))
          throw new PyError('unsupported', 'Une seule boucle par compréhension.', this.tok.line);
        this.expectOp(']');
        return { k: 'comp', elt: first, target, iter, cond, line };
      }
      items.push(first);
      while (this.isOp(',')) {
        this.i++;
        if (this.isOp(']')) break;
        items.push(this.expr());
      }
      this.expectOp(']');
      return { k: 'list', items, line };
    }
    if (this.isOp('{'))
      throw new PyError('unsupported', 'Les dictionnaires ne sont pas pris en charge.', line);
    if (t.t === 'newline' || t.t === 'eof') this.fail('Expression incomplète.');
    this.fail(`« ${t.v} » inattendu.`);
  }
}

function toTarget(e: Expr): Target {
  if (e.k === 'name') return { k: 'name', id: e.id, line: e.line };
  if (e.k === 'tuple') return { k: 'tuple', items: e.items.map(toTarget), line: e.line };
  if (e.k === 'index') return { k: 'index', e: e.e, i: e.i, line: e.line };
  throw new PyError('syntax', 'On ne peut pas affecter une valeur à cette expression.', e.line);
}

/** Programme → arbre. Lève `PyError` (code 'syntax' ou 'unsupported'). */
export function parseProgram(src: string): Module {
  return new Parser(tokenize(src)).module();
}

/** Une expression seule (un appel « pgcd(84, 36) »). */
export function parseExpression(src: string): Expr {
  const p = new Parser(tokenize(src));
  const e = p.expr();
  return e;
}
